import React from "react";
const { useEffect: useEffect_HT, useRef: useRef_HT } = React;

function HeroThree() {
  const mountRef = useRef_HT(null);
  const cardRef = useRef_HT(null);
  const canvasRef = useRef_HT(null);

  useEffect_HT(() => {
    const mount = mountRef.current;
    const card = cardRef.current;
    const canvas = canvasRef.current;
    if (!mount || !card || !canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const prefersReducedMotion =
      window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    function readToken(name, fallback) {
      try {
        const probe = document.createElement("div");
        probe.style.color = `var(${name})`;
        probe.style.position = "absolute";
        probe.style.visibility = "hidden";
        document.body.appendChild(probe);
        const c = getComputedStyle(probe).color;
        document.body.removeChild(probe);
        return c && c !== "rgba(0, 0, 0, 0)" ? c : fallback;
      } catch { return fallback; }
    }

    const COLOR_INDIGO      = readToken("--indigo",      "rgb(74, 78, 158)");
    const COLOR_INDIGO_DEEP = readToken("--indigo-deep", "rgb(56, 56, 102)");
    const COLOR_VERMILION   = readToken("--vermilion",   "rgb(198, 96, 61)");
    const COLOR_FG          = readToken("--fg",          "rgb(20, 24, 30)");

    function parseRgb(s) {
      const m = String(s).match(/\d+(?:\.\d+)?/g);
      if (!m || m.length < 3) return [20, 24, 30];
      return [parseFloat(m[0]), parseFloat(m[1]), parseFloat(m[2])];
    }
    const RGB_INDIGO      = parseRgb(COLOR_INDIGO);
    const RGB_INDIGO_DEEP = parseRgb(COLOR_INDIGO_DEEP);
    const RGB_VERMILION   = parseRgb(COLOR_VERMILION);
    const RGB_FG          = parseRgb(COLOR_FG);

    function rgba(rgb, a) {
      return `rgba(${rgb[0]|0}, ${rgb[1]|0}, ${rgb[2]|0}, ${a})`;
    }

    let DPR = Math.min(window.devicePixelRatio || 1, 2);
    let W = 0, H = 0;

    function resize() {
      const r = mount.getBoundingClientRect();
      W = Math.max(1, Math.floor(r.width));
      H = Math.max(1, Math.floor(r.height));
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = W * DPR;
      canvas.height = H * DPR;
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(mount);

    // Nodes drift in pseudo-3D space. z scales size + opacity for depth.
    const isMobile = window.matchMedia && window.matchMedia("(max-width: 720px)").matches;
    const NODE_COUNT = isMobile ? 56 : 90;
    const ACCENT_EVERY = 14; // every Nth node is a vermilion accent

    const nodes = [];
    for (let i = 0; i < NODE_COUNT; i++) {
      const z = 0.35 + Math.random() * 0.65; // 0.35..1
      nodes.push({
        x: Math.random(),
        y: Math.random(),
        z,
        vx: (Math.random() - 0.5) * 0.00018 * z,
        vy: (Math.random() - 0.5) * 0.00018 * z,
        accent: i % ACCENT_EVERY === 0,
      });
    }

    const mouse = { tx: 0, ty: 0, x: 0, y: 0, inside: false };
    function onMove(e) {
      const r = card.getBoundingClientRect();
      const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      mouse.inside = inside;
      if (!inside) { mouse.tx = 0; mouse.ty = 0; return; }
      const px = (e.clientX - r.left) / r.width;
      const py = (e.clientY - r.top) / r.height;
      mouse.tx = (px - 0.5) * 2;
      mouse.ty = (py - 0.5) * 2;
    }
    function onLeave() { mouse.inside = false; mouse.tx = 0; mouse.ty = 0; }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseout", onLeave);

    let intersecting = true;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) intersecting = e.isIntersecting;
    }, { threshold: 0.01 });
    io.observe(card);

    function project(n) {
      // soft parallax: deeper nodes (higher z) shift more
      const par = 14 * n.z;
      return {
        x: n.x * W + mouse.x * par,
        y: n.y * H + mouse.y * par,
      };
    }

    function render() {
      ctx.clearRect(0, 0, W, H);

      // lines first, so dots sit on top
      const LINK_DIST = isMobile ? 110 : 140;
      const LD2 = LINK_DIST * LINK_DIST;

      for (let i = 0; i < nodes.length; i++) {
        const a = nodes[i];
        const pa = project(a);
        for (let j = i + 1; j < nodes.length; j++) {
          const b = nodes[j];
          const pb = project(b);
          const dx = pa.x - pb.x;
          const dy = pa.y - pb.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > LD2) continue;
          const t = 1 - d2 / LD2;
          const depth = (a.z + b.z) * 0.5;
          const accent = a.accent || b.accent;
          const rgb = accent ? RGB_VERMILION : RGB_INDIGO_DEEP;
          const baseA = accent ? 0.32 : 0.18;
          const alpha = baseA * t * depth;
          ctx.strokeStyle = rgba(rgb, alpha);
          ctx.lineWidth = accent ? 0.9 : 0.6;
          ctx.beginPath();
          ctx.moveTo(pa.x, pa.y);
          ctx.lineTo(pb.x, pb.y);
          ctx.stroke();
        }
      }

      // dots
      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        const p = project(n);
        const r = (n.accent ? 2.8 : 1.6) * n.z;
        const a = (n.accent ? 0.95 : 0.6) * n.z;

        if (n.accent) {
          // soft halo around accent nodes
          const haloR = 18 * n.z;
          const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, haloR);
          grad.addColorStop(0, rgba(RGB_VERMILION, 0.22));
          grad.addColorStop(1, rgba(RGB_VERMILION, 0));
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(p.x, p.y, haloR, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = rgba(RGB_VERMILION, a);
        } else {
          ctx.fillStyle = rgba(RGB_INDIGO, a);
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    if (prefersReducedMotion) {
      render();
      return () => {
        ro.disconnect(); io.disconnect();
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseout", onLeave);
      };
    }

    let raf = 0;
    let lastT = performance.now();

    function frame(now) {
      raf = requestAnimationFrame(frame);
      if (!intersecting || document.hidden) { lastT = now; return; }
      const dt = Math.min(64, now - lastT);
      lastT = now;

      mouse.x += (mouse.tx - mouse.x) * 0.05;
      mouse.y += (mouse.ty - mouse.y) * 0.05;

      for (let i = 0; i < nodes.length; i++) {
        const n = nodes[i];
        n.x += n.vx * dt;
        n.y += n.vy * dt;
        if (n.x < -0.05) n.x = 1.05; else if (n.x > 1.05) n.x = -0.05;
        if (n.y < -0.05) n.y = 1.05; else if (n.y > 1.05) n.y = -0.05;
      }

      render();
    }
    raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className="hero-three-card"
      style={{
        position: "relative", flex: 1, width: "100%",
        minHeight: 380, overflow: "hidden", pointerEvents: "none",
      }}
    >
      <div ref={mountRef} aria-hidden="true" style={{ position: "absolute", inset: 0 }}>
        <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
      </div>
    </div>
  );
}

export { HeroThree };
